import { useEffect, useState } from 'react'
import { Card } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { usePetName } from '@/hooks/usePetName'

interface Session {
  id: string
  title: string | null
  summary: string | null
  message_count: number
  started_at: string
  last_active_at: string
  ended_at: string | null
}

interface Interaction {
  id: number
  user_message: string | null
  assistant_message: string | null
  created_at: string
}

/** /partner/history — 相棒との対話履歴 (chat_sessions 一覧) */
export default function PartnerHistory() {
  const { name: petName } = usePetName()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState<string | null>(null)
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [loadingInteractions, setLoadingInteractions] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      const { data } = await supabase
        .from('chat_sessions')
        .select('id, title, summary, message_count, started_at, last_active_at, ended_at')
        .order('started_at', { ascending: false })
        .limit(100)
      if (alive) {
        setSessions((data || []) as Session[])
        setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  const open = async (id: string) => {
    if (openId === id) {
      setOpenId(null)
      setInteractions([])
      return
    }
    setOpenId(id)
    setLoadingInteractions(true)
    setInteractions([])
    const { data } = await supabase
      .from('chat_interactions')
      .select('id, user_message, assistant_message, created_at')
      .eq('session_id', id)
      .order('created_at', { ascending: true })
    setInteractions((data || []) as Interaction[])
    setLoadingInteractions(false)
  }

  return (
    <div style={{ padding: '24px 16px', maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 8 }}>{petName} との対話履歴</h1>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 20 }}>
        過去の対話セッションです。クリックで会話の中身が見えます。
      </div>

      {loading && <div style={{ fontSize: 13, color: 'var(--text3)' }}>読み込み中…</div>}
      {!loading && sessions.length === 0 && (
        <Card style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>
          まだ会話がありません
        </Card>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sessions.map((s) => {
          const date = (s.started_at || '').slice(0, 10)
          const isActive = !s.ended_at
          return (
            <Card key={s.id} style={{ padding: 12 }}>
              <button
                type="button"
                onClick={() => open(s.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  padding: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--text2)' }}>{date}</span>
                  {isActive && (
                    <span style={{ fontSize: 10, color: 'var(--accent, #5046E5)', padding: '1px 6px', border: '1px solid var(--accent, #5046E5)', borderRadius: 4 }}>
                      active
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 'auto' }}>{s.message_count}件</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                  {s.summary || s.title || '(無題)'}
                </div>
              </button>

              {openId === s.id && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  {loadingInteractions && <div style={{ fontSize: 11, color: 'var(--text3)' }}>読み込み中…</div>}
                  {!loadingInteractions && interactions.map((it) => (
                    <div key={it.id} style={{ marginBottom: 12, fontSize: 12, lineHeight: 1.6 }}>
                      {it.user_message && (
                        <div style={{ background: 'var(--surface2)', padding: 8, borderRadius: 6, marginBottom: 6, whiteSpace: 'pre-wrap' }}>
                          {it.user_message}
                        </div>
                      )}
                      {it.assistant_message && (
                        <div style={{ background: 'var(--surface)', padding: 8, borderRadius: 6, border: '1px solid var(--border)', whiteSpace: 'pre-wrap' }}>
                          {it.assistant_message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}
